import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AreaChart, Area, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import { Plus, Loader2, Moon, HeartPulse, Footprints, Activity, Gauge } from "lucide-react";

const SOURCES = ["Oura Ring", "WHOOP", "Apple Watch", "Garmin", "Fitbit", "manual"];

export default function WearablesTab({ patientId }) {
  const [data, setData] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    date: new Date().toISOString().slice(0, 10), source: "Oura Ring",
    sleep_hours: "", resting_hr: "", steps: "", hrv: "", spo2: "", bp_sys: "", bp_dia: "", weight: "", ecg_note: "",
  });

  const load = () => api.get(`/patients/${patientId}/wearables`).then((r) => setData(r.data));
  useEffect(() => { load(); }, [patientId]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const num = (v) => (v === "" || v == null ? null : parseFloat(v));

  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/patients/${patientId}/wearables`, {
        date: form.date, source: form.source,
        sleep_hours: num(form.sleep_hours), resting_hr: num(form.resting_hr),
        steps: num(form.steps), hrv: num(form.hrv), spo2: num(form.spo2),
        bp_sys: num(form.bp_sys), bp_dia: num(form.bp_dia), weight: num(form.weight),
        ecg_note: form.ecg_note || null,
      });
      toast.success("Dados registrados");
      setOpen(false);
      setForm({ ...form, sleep_hours: "", resting_hr: "", steps: "", hrv: "", spo2: "", bp_sys: "", bp_dia: "", weight: "", ecg_note: "" });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const latest = data[data.length - 1] || {};
  const kpis = [
    { icon: Moon, label: "Sono (h)", value: latest.sleep_hours ?? "—", color: "text-chart-5" },
    { icon: HeartPulse, label: "FC repouso", value: latest.resting_hr ?? "—", color: "text-destructive" },
    { icon: Activity, label: "HRV (ms)", value: latest.hrv ?? "—", color: "text-ai" },
    { icon: Gauge, label: "PA (mmHg)", value: (latest.bp_sys && latest.bp_dia) ? `${latest.bp_sys}/${latest.bp_dia}` : "—", color: "text-warning" },
    { icon: Footprints, label: "Passos", value: latest.steps ?? "—", color: "text-chart-3" },
  ];

  const tip = { background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="font-heading text-xl font-medium">Wearables e sinais vitais</h3>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-wearable-btn"><Plus className="w-4 h-4 mr-2" /> Adicionar dados</Button>
          </DialogTrigger>
          <DialogContent data-testid="wearable-dialog">
            <DialogHeader><DialogTitle>Registrar dados de wearable</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-4 py-2">
              <div className="space-y-2">
                <Label>Data</Label>
                <Input type="date" value={form.date} onChange={set("date")} data-testid="wearable-date-input" />
              </div>
              <div className="space-y-2">
                <Label>Fonte</Label>
                <Select value={form.source} onValueChange={(v) => setForm({ ...form, source: v })}>
                  <SelectTrigger data-testid="wearable-source-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{SOURCES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2"><Label>Sono (horas)</Label>
                <Input type="number" step="any" value={form.sleep_hours} onChange={set("sleep_hours")} data-testid="wearable-sleep-input" /></div>
              <div className="space-y-2"><Label>FC repouso (bpm)</Label>
                <Input type="number" value={form.resting_hr} onChange={set("resting_hr")} data-testid="wearable-hr-input" /></div>
              <div className="space-y-2"><Label>HRV (ms)</Label>
                <Input type="number" value={form.hrv} onChange={set("hrv")} data-testid="wearable-hrv-input" /></div>
              <div className="space-y-2"><Label>SpO2 (%)</Label>
                <Input type="number" step="any" value={form.spo2} onChange={set("spo2")} data-testid="wearable-spo2-input" /></div>
              <div className="space-y-2"><Label>PA sistólica</Label>
                <Input type="number" value={form.bp_sys} onChange={set("bp_sys")} data-testid="wearable-bpsys-input" placeholder="ex: 120" /></div>
              <div className="space-y-2"><Label>PA diastólica</Label>
                <Input type="number" value={form.bp_dia} onChange={set("bp_dia")} data-testid="wearable-bpdia-input" placeholder="ex: 80" /></div>
              <div className="space-y-2"><Label>Peso (kg)</Label>
                <Input type="number" step="any" value={form.weight} onChange={set("weight")} data-testid="wearable-weight-input" placeholder="ex: 72.5" /></div>
              <div className="space-y-2"><Label>Passos</Label>
                <Input type="number" value={form.steps} onChange={set("steps")} data-testid="wearable-steps-input" /></div>
              <div className="space-y-2"><Label>Nota ECG</Label>
                <Input value={form.ecg_note} onChange={set("ecg_note")} data-testid="wearable-ecg-input" placeholder="ex: ritmo sinusal" /></div>
            </div>
            <DialogFooter>
              <Button onClick={save} disabled={saving} data-testid="save-wearable-btn">
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Salvar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {kpis.map((k) => {
          const Icon = k.icon;
          return (
            <Card key={k.label} className="p-5 border-border shadow-none" data-testid={`wearable-kpi-${k.label}`}>
              <Icon className={`w-5 h-5 ${k.color} mb-3`} />
              <div className="font-mono-data text-2xl font-semibold">{k.value}</div>
              <div className="text-xs text-muted-foreground mt-1">{k.label}</div>
            </Card>
          );
        })}
      </div>

      {data.length === 0 ? (
        <Card className="p-10 text-center text-muted-foreground border-dashed">
          Sem dados de wearables. Conecte um Oura Ring, WHOOP ou insira manualmente.
        </Card>
      ) : (
        <div className="grid lg:grid-cols-2 gap-4">
          <Card className="p-6 border-border shadow-none">
            <h4 className="font-heading font-medium mb-4 flex items-center gap-2"><Moon className="w-4 h-4 text-chart-5" /> Sono</h4>
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={data}>
                <defs>
                  <linearGradient id="gSleep" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="hsl(var(--chart-5))" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="hsl(var(--chart-5))" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} />
                <Tooltip contentStyle={tip} />
                <Area type="monotone" dataKey="sleep_hours" name="Sono (h)" stroke="hsl(var(--chart-5))" fill="url(#gSleep)" strokeWidth={2.5} />
              </AreaChart>
            </ResponsiveContainer>
          </Card>
          <Card className="p-6 border-border shadow-none">
            <h4 className="font-heading font-medium mb-4 flex items-center gap-2"><HeartPulse className="w-4 h-4 text-destructive" /> Batimentos & HRV</h4>
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={data}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} />
                <Tooltip contentStyle={tip} />
                <Line type="monotone" dataKey="resting_hr" name="FC repouso" stroke="hsl(var(--destructive))" strokeWidth={2.5} dot={{ r: 3 }} />
                <Line type="monotone" dataKey="hrv" name="HRV" stroke="hsl(var(--accent-ai))" strokeWidth={2.5} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </Card>
          <Card className="p-6 border-border shadow-none">
            <h4 className="font-heading font-medium mb-4 flex items-center gap-2"><Gauge className="w-4 h-4 text-warning" /> Pressão arterial</h4>
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={data}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} />
                <Tooltip contentStyle={tip} />
                <Line type="monotone" dataKey="bp_sys" name="Sistólica" stroke="hsl(var(--destructive))" strokeWidth={2.5} dot={{ r: 3 }} />
                <Line type="monotone" dataKey="bp_dia" name="Diastólica" stroke="hsl(var(--chart-3))" strokeWidth={2.5} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </Card>
        </div>
      )}
    </div>
  );
}
